import React, { useState, useEffect } from "react"
import {
    Box,
    Button,
    FormControl,
    Grid,
    InputLabel,
    MenuItem,
    Select,
    TextField,
    Typography,
    Alert,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
} from "@mui/material"
import { useTranslation } from "react-i18next"
import apiClient from "../../services/apiClient"
import * as adminService from "../../services/adminService"
import { getValidationError } from "../../utils/customerValidation"

interface CustomerFormProps {
    companyId: string
    initialData?: any
    onSubmit: (data: any) => Promise<void>
    onCancel: () => void
    submitLabel?: string
}

const CustomerForm: React.FC<CustomerFormProps> = ({
    companyId,
    initialData,
    onSubmit,
    onCancel,
    submitLabel,
}) => {
    const { t } = useTranslation()
    const [customerTypes, setCustomerTypes] = useState<any[]>([])
    const [formData, setFormData] = useState({
        type: "individual",
        name: "",
        company_name: "",
        email: "",
        phone: "",
        address_street: "",
        zip_code: "",
        city: "",
        country: "",
        siret: "",
        vat_number: "",
        customer_type_id: "",
    })
    const [errors, setErrors] = useState<Record<string, string>>({})
    const [serverError, setServerError] = useState("")
    const [duplicateWarning, setDuplicateWarning] = useState("")
    const [submitting, setSubmitting] = useState(false)
    const [typeChangeWarning, setTypeChangeWarning] = useState(false)
    const [pendingType, setPendingType] = useState("")

    useEffect(() => {
        if (initialData) {
            setFormData({
                type: initialData.type || "individual",
                name: initialData.name || "",
                company_name: initialData.company_name || "",
                email: initialData.email || "",
                phone: initialData.phone || "",
                address_street: initialData.address_street || "",
                zip_code: initialData.zip_code || "",
                city: initialData.city || "",
                country: initialData.country || "",
                siret: initialData.siret || "",
                vat_number: initialData.vat_number || "",
                customer_type_id: initialData.customer_type_id || "",
            })
        }
    }, [initialData])

    useEffect(() => {
        if (!companyId) return
        let cancelled = false
        ;(async () => {
            try {
                const res = await apiClient.get(
                    `/api/companies/${companyId}/customer-types`
                )
                if (cancelled) return
                setCustomerTypes(res.customerTypes || [])
            } catch (e: any) {
                console.error("Failed to load customer types:", e)
            }
        })()
        return () => {
            cancelled = true
        }
    }, [companyId])

    const handleEmailBlur = async () => {
        if (!formData.email || !companyId) {
            setDuplicateWarning("")
            return
        }
        try {
            const result = await adminService.checkDuplicateEmail(
                companyId,
                formData.email,
                initialData?.id
            )
            if (result.hasDuplicate) {
                setDuplicateWarning(
                    String(
                        t("customerForm.warnings.duplicateEmail", {
                            email: formData.email,
                        })
                    )
                )
            } else {
                setDuplicateWarning("")
            }
        } catch (e) {
            // Silently ignore duplicate check failures
        }
    }

    const setField = (field: string, value: string) => {
        setFormData((prev) => ({ ...prev, [field]: value }))
        if (errors[field]) {
            setErrors((prev) => {
                const next = { ...prev }
                delete next[field]
                return next
            })
        }
    }

    const handleTypeChange = (newType: string) => {
        if (
            initialData &&
            initialData.type === "business" &&
            newType === "individual" &&
            (formData.company_name || formData.siret || formData.vat_number)
        ) {
            setPendingType(newType)
            setTypeChangeWarning(true)
        } else {
            setField("type", newType)
        }
    }

    const confirmTypeChange = () => {
        setFormData((prev) => ({
            ...prev,
            type: pendingType,
            company_name: "",
            siret: "",
            vat_number: "",
        }))
        setTypeChangeWarning(false)
        setPendingType("")
    }

    const validate = (): boolean => {
        const newErrors: Record<string, string> = {}

        if (formData.type === "individual" && !formData.name.trim()) {
            newErrors.name = String(t("customerForm.errors.nameRequired"))
        }
        if (formData.type === "business" && !formData.company_name.trim()) {
            newErrors.company_name = String(
                t("customerForm.errors.companyNameRequired")
            )
        }

        const siretErr = getValidationError("siret", formData.siret)
        if (siretErr) newErrors.siret = siretErr

        const vatErr = getValidationError("vat_number", formData.vat_number)
        if (vatErr) newErrors.vat_number = vatErr

        const zipErr = getValidationError("zip_code", formData.zip_code)
        if (zipErr) newErrors.zip_code = zipErr

        setErrors(newErrors)
        return Object.keys(newErrors).length === 0
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        setServerError("")

        if (!validate()) return

        setSubmitting(true)
        try {
            await onSubmit({
                ...formData,
                customer_type_id: formData.customer_type_id || null,
            })
        } catch (err: any) {
            const msg =
                err?.response?.data?.message ||
                err?.message ||
                String(t("customerForm.errors.saveFailed"))
            setServerError(msg)

            if (err?.response?.data?.details) {
                const serverErrors: Record<string, string> = {}
                const details = err.response.data.details
                if (typeof details === "object") {
                    for (const [key, val] of Object.entries(details)) {
                        serverErrors[key] = String(val)
                    }
                }
                if (Object.keys(serverErrors).length > 0) {
                    setErrors((prev) => ({ ...prev, ...serverErrors }))
                }
            }
        } finally {
            setSubmitting(false)
        }
    }

    const isBusiness = formData.type === "business"

    return (
        <Box component="form" onSubmit={handleSubmit} noValidate>
            {serverError && (
                <Alert severity="error" sx={{ mb: 3 }}>
                    {serverError}
                </Alert>
            )}
            {duplicateWarning && (
                <Alert severity="warning" sx={{ mb: 3 }}>
                    {duplicateWarning}
                </Alert>
            )}

            <Grid container spacing={3}>
                {/* Type Selection */}
                <Grid item xs={12} md={6}>
                    <FormControl fullWidth required>
                        <InputLabel>
                            {t("customerForm.fields.legalForm")}
                        </InputLabel>
                        <Select
                            value={formData.type}
                            label={t("customerForm.fields.legalForm")}
                            onChange={(e) => handleTypeChange(e.target.value)}
                        >
                            <MenuItem value="individual">
                                {t("customerForm.types.individual")}
                            </MenuItem>
                            <MenuItem value="business">
                                {t("customerForm.types.business")}
                            </MenuItem>
                        </Select>
                    </FormControl>
                </Grid>

                <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                        <InputLabel>
                            {t("customerForm.fields.customerType")}
                        </InputLabel>
                        <Select
                            value={formData.customer_type_id}
                            label={t("customerForm.fields.customerType")}
                            onChange={(e) =>
                                setField("customer_type_id", e.target.value)
                            }
                        >
                            <MenuItem value="">
                                {t("customerForm.fields.none")}
                            </MenuItem>
                            {customerTypes.map((ct) => (
                                <MenuItem key={ct.id} value={ct.id}>
                                    {ct.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>

                {/* Identity */}
                <Grid item xs={12}>
                    <Typography
                        variant="subtitle2"
                        sx={{ mb: 1, color: "#6b7280" }}
                    >
                        {t("customerForm.sections.identity")}
                    </Typography>
                </Grid>

                <Grid item xs={12} md={6}>
                    <TextField
                        label={t("customerForm.fields.name")}
                        fullWidth
                        required={!isBusiness}
                        value={formData.name}
                        onChange={(e) => setField("name", e.target.value)}
                        error={!!errors.name}
                        helperText={errors.name}
                    />
                </Grid>

                {isBusiness && (
                    <Grid item xs={12} md={6}>
                        <TextField
                            label={t("customerForm.fields.companyName")}
                            fullWidth
                            required
                            value={formData.company_name}
                            onChange={(e) =>
                                setField("company_name", e.target.value)
                            }
                            error={!!errors.company_name}
                            helperText={errors.company_name}
                        />
                    </Grid>
                )}

                {/* Contact */}
                <Grid item xs={12}>
                    <Typography
                        variant="subtitle2"
                        sx={{ mb: 1, color: "#6b7280" }}
                    >
                        {t("customerForm.sections.contact")}
                    </Typography>
                </Grid>

                <Grid item xs={12} md={6}>
                    <TextField
                        label={t("customerForm.fields.email")}
                        type="email"
                        fullWidth
                        value={formData.email}
                        onChange={(e) => setField("email", e.target.value)}
                        onBlur={handleEmailBlur}
                        error={!!errors.email}
                        helperText={errors.email}
                    />
                </Grid>

                <Grid item xs={12} md={6}>
                    <TextField
                        label={t("customerForm.fields.phone")}
                        fullWidth
                        value={formData.phone}
                        onChange={(e) => setField("phone", e.target.value)}
                        error={!!errors.phone}
                        helperText={errors.phone}
                    />
                </Grid>

                {/* Address */}
                <Grid item xs={12}>
                    <Typography
                        variant="subtitle2"
                        sx={{ mb: 1, color: "#6b7280" }}
                    >
                        {t("customerForm.sections.address")}
                    </Typography>
                </Grid>

                <Grid item xs={12}>
                    <TextField
                        label={t("customerForm.fields.street")}
                        fullWidth
                        value={formData.address_street}
                        onChange={(e) =>
                            setField("address_street", e.target.value)
                        }
                    />
                </Grid>

                <Grid item xs={12} md={4}>
                    <TextField
                        label={t("customerForm.fields.zipCode")}
                        fullWidth
                        value={formData.zip_code}
                        onChange={(e) => setField("zip_code", e.target.value)}
                        error={!!errors.zip_code}
                        helperText={errors.zip_code}
                    />
                </Grid>

                <Grid item xs={12} md={4}>
                    <TextField
                        label={t("customerForm.fields.city")}
                        fullWidth
                        value={formData.city}
                        onChange={(e) => setField("city", e.target.value)}
                    />
                </Grid>

                <Grid item xs={12} md={4}>
                    <TextField
                        label={t("customerForm.fields.country")}
                        fullWidth
                        value={formData.country}
                        onChange={(e) => setField("country", e.target.value)}
                    />
                </Grid>

                {/* Business Fields (conditional) */}
                {isBusiness && (
                    <>
                        <Grid item xs={12}>
                            <Typography
                                variant="subtitle2"
                                sx={{ mb: 1, color: "#6b7280" }}
                            >
                                {t("customerForm.sections.businessInfo")}
                            </Typography>
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <TextField
                                label="SIRET"
                                fullWidth
                                value={formData.siret}
                                onChange={(e) =>
                                    setField("siret", e.target.value)
                                }
                                error={!!errors.siret}
                                helperText={
                                    errors.siret ||
                                    t("customerForm.hints.siret")
                                }
                                inputProps={{ maxLength: 14 }}
                            />
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <TextField
                                label={t("customerForm.fields.vatNumber")}
                                fullWidth
                                value={formData.vat_number}
                                onChange={(e) =>
                                    setField("vat_number", e.target.value)
                                }
                                error={!!errors.vat_number}
                                helperText={
                                    errors.vat_number ||
                                    t("customerForm.hints.vatNumber")
                                }
                            />
                        </Grid>
                    </>
                )}
            </Grid>

            {/* Actions */}
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    gap: 2,
                    mt: 4,
                }}
            >
                <Button onClick={onCancel} disabled={submitting}>
                    {t("common.cancel")}
                </Button>
                <Button type="submit" variant="contained" disabled={submitting}>
                    {submitting
                        ? t("common.saving")
                        : submitLabel || t("common.save")}
                </Button>
            </Box>

            {/* Type Change Warning Dialog */}
            <Dialog
                open={typeChangeWarning}
                onClose={() => setTypeChangeWarning(false)}
            >
                <DialogTitle>
                    {t("customerForm.typeChangeDialog.title")}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {t("customerForm.typeChangeDialog.description")}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setTypeChangeWarning(false)}>
                        {t("common.cancel")}
                    </Button>
                    <Button
                        onClick={confirmTypeChange}
                        color="warning"
                        variant="contained"
                    >
                        {t("customerForm.typeChangeDialog.confirm")}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    )
}

export default CustomerForm
